import * as THREE from 'three';

/** Shared procedural prop builders — every location is built from these,
 * no external 3D assets. */

export function stdMat(
  color: THREE.ColorRepresentation,
  roughness = 0.85,
  metalness = 0,
): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness, metalness });
}

export function box(
  w: number,
  h: number,
  d: number,
  material: THREE.Material,
  x = 0,
  y = 0,
  z = 0,
  ry = 0,
): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), material);
  mesh.position.set(x, y, z);
  mesh.rotation.y = ry;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/** Axis-aligned collider box helper (y from y0 to y1). */
export function collider(
  cx: number,
  cz: number,
  w: number,
  d: number,
  y0: number,
  y1: number,
): THREE.Box3 {
  return new THREE.Box3(
    new THREE.Vector3(cx - w / 2, y0, cz - d / 2),
    new THREE.Vector3(cx + w / 2, y1, cz + d / 2),
  );
}

/** Snow-dusted pine tree. Returns the group; adds a trunk collider to `colliders`. */
export function pine(
  x: number,
  z: number,
  groundY: number,
  scale: number,
  colliders: THREE.Box3[],
): THREE.Group {
  const g = new THREE.Group();
  const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.2, 1, 8), stdMat(0x5a4433));
  trunk.position.y = 0.5;
  trunk.castShadow = true;
  g.add(trunk);
  const green = stdMat(0x2f5443, 0.95);
  const snow = stdMat(0xe9eef6, 0.95);
  for (let i = 0; i < 3; i++) {
    const r = 1.35 - i * 0.36;
    const cone = new THREE.Mesh(new THREE.ConeGeometry(r, 1.5, 9), green);
    cone.position.y = 1.15 + i * 0.85;
    cone.castShadow = true;
    g.add(cone);
    const dust = new THREE.Mesh(new THREE.ConeGeometry(r * 0.75, 0.5, 9), snow);
    dust.position.y = 1.15 + i * 0.85 + 0.55;
    g.add(dust);
  }
  g.position.set(x, groundY, z);
  g.scale.setScalar(scale);
  colliders.push(collider(x, z, 0.6 * scale, 0.6 * scale, groundY, groundY + 2 * scale));
  return g;
}

export function rock(x: number, z: number, groundY: number, scale: number): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.IcosahedronGeometry(scale, 1), stdMat(0x8b8f98, 0.95));
  mesh.position.set(x, groundY + scale * 0.35, z);
  mesh.scale.y = 0.65;
  mesh.rotation.y = x * 1.7 + z;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/** Warm streetlight with a real light source. */
export function streetlight(x: number, z: number, groundY: number): THREE.Group {
  const g = new THREE.Group();
  const metal = stdMat(0x30343c, 0.6, 0.6);
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.09, 4.6, 8), metal);
  pole.position.y = 2.3;
  pole.castShadow = true;
  g.add(pole);
  const arm = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.08, 0.08), metal);
  arm.position.set(0.42, 4.55, 0);
  g.add(arm);
  const bulb = new THREE.Mesh(
    new THREE.SphereGeometry(0.14, 10, 8),
    new THREE.MeshStandardMaterial({
      color: 0xffd9a0,
      emissive: 0xffb35c,
      emissiveIntensity: 2.2,
    }),
  );
  bulb.position.set(0.82, 4.45, 0);
  g.add(bulb);
  const light = new THREE.PointLight(0xffb35c, 90, 20, 2);
  light.position.set(0.82, 4.4, 0);
  g.add(light);
  g.position.set(x, groundY, z);
  return g;
}

export function bench(x: number, z: number, groundY: number, ry: number): THREE.Group {
  const g = new THREE.Group();
  const wood = stdMat(0x6e5642, 0.9);
  const metal = stdMat(0x2c3038, 0.6, 0.5);
  g.add(box(1.8, 0.07, 0.5, wood, 0, 0.48, 0));
  g.add(box(1.8, 0.5, 0.07, wood, 0, 0.78, -0.26));
  g.add(box(0.08, 0.48, 0.45, metal, -0.8, 0.24, 0));
  g.add(box(0.08, 0.48, 0.45, metal, 0.8, 0.24, 0));
  // Snow on the seat.
  g.add(box(1.8, 0.05, 0.5, stdMat(0xeef2f8, 0.95), 0, 0.54, 0));
  g.position.set(x, groundY, z);
  g.rotation.y = ry;
  return g;
}

export interface CarResult {
  group: THREE.Group;
  /** Snow slabs on the hood and roof — registered as paintable surfaces. */
  paintSlabs: { id: string; mesh: THREE.Mesh }[];
}

/** Parked car with paintable snow on its hood and roof.
 * `makeSnowMat` lets the caller bind each slab to its own paint texture. */
export function car(
  idPrefix: string,
  bodyColor: THREE.ColorRepresentation,
  x: number,
  z: number,
  groundY: number,
  ry: number,
  colliders: THREE.Box3[],
  makeSnowMat: (surfaceId: string) => {
    material: THREE.Material;
    register: (m: THREE.Mesh) => void;
  },
): CarResult {
  const g = new THREE.Group();
  const body = stdMat(bodyColor, 0.4, 0.35);
  const glass = new THREE.MeshStandardMaterial({
    color: 0x9fb6c9,
    roughness: 0.15,
    metalness: 0.4,
  });
  g.add(box(1.85, 0.55, 4.2, body, 0, 0.62, 0));
  g.add(box(1.7, 0.5, 2.1, glass, 0, 1.12, -0.15));
  const wheelGeo = new THREE.CylinderGeometry(0.34, 0.34, 0.25, 14);
  wheelGeo.rotateZ(Math.PI / 2);
  const wheelMat = stdMat(0x1c1e22, 0.95);
  for (const [wx, wz] of [
    [-0.85, 1.35],
    [0.85, 1.35],
    [-0.85, -1.35],
    [0.85, -1.35],
  ] as const) {
    const wheel = new THREE.Mesh(wheelGeo, wheelMat);
    wheel.position.set(wx, 0.34, wz);
    g.add(wheel);
  }

  const paintSlabs: CarResult['paintSlabs'] = [];
  const slab = (id: string, w: number, d: number, sx: number, sy: number, sz: number) => {
    const bound = makeSnowMat(id);
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, d), bound.material);
    mesh.geometry.rotateX(-Math.PI / 2);
    mesh.position.set(sx, sy, sz);
    g.add(mesh);
    paintSlabs.push({ id, mesh });
    bound.register(mesh);
  };
  slab(`${idPrefix}-roof`, 1.66, 2.0, 0, 1.38, -0.15);
  slab(`${idPrefix}-hood`, 1.8, 1.15, 0, 0.91, 1.5);

  g.position.set(x, groundY, z);
  g.rotation.y = ry;
  colliders.push(
    collider(
      x,
      z,
      ry % Math.PI < 0.3 ? 2.0 : 4.3,
      ry % Math.PI < 0.3 ? 4.3 : 2.0,
      groundY,
      groundY + 1.5,
    ),
  );
  return { group: g, paintSlabs };
}

let facadeSeedCounter = 1;

/** City building with a generated facade texture: dark walls, warm lit windows. */
export function building(
  w: number,
  h: number,
  d: number,
  x: number,
  z: number,
  groundY: number,
  colliders: THREE.Box3[] | null,
  tint = '#3a4254',
): THREE.Mesh {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 256;
  const g2d = canvas.getContext('2d')!;
  g2d.fillStyle = tint;
  g2d.fillRect(0, 0, 128, 256);
  const emissiveCanvas = document.createElement('canvas');
  emissiveCanvas.width = 128;
  emissiveCanvas.height = 256;
  const e2d = emissiveCanvas.getContext('2d')!;
  e2d.fillStyle = '#000';
  e2d.fillRect(0, 0, 128, 256);

  facadeSeedCounter = (facadeSeedCounter * 16807) % 2147483647;
  let s = facadeSeedCounter;
  const rand = () => {
    s = (s * 16807) % 2147483647;
    return s / 2147483647;
  };
  const cols = 5;
  const rows = 9;
  for (let cy = 0; cy < rows; cy++) {
    for (let cx = 0; cx < cols; cx++) {
      const wx = 10 + cx * 23;
      const wy = 14 + cy * 26;
      g2d.fillStyle = '#242b3a';
      g2d.fillRect(wx, wy, 14, 17);
      if (rand() < 0.42) {
        const warm = rand() < 0.8;
        const c = warm ? '#ffc87a' : '#bcd8ff';
        g2d.fillStyle = c;
        g2d.fillRect(wx + 1, wy + 1, 12, 15);
        e2d.fillStyle = c;
        e2d.fillRect(wx + 1, wy + 1, 12, 15);
      }
    }
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const etex = new THREE.CanvasTexture(emissiveCanvas);
  etex.colorSpace = THREE.SRGBColorSpace;
  const mat = new THREE.MeshStandardMaterial({
    map: tex,
    emissive: 0xffffff,
    emissiveMap: etex,
    emissiveIntensity: 0.85,
    roughness: 0.9,
  });
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  mesh.position.set(x, groundY + h / 2, z);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  colliders?.push(collider(x, z, w, d, groundY, groundY + h));
  return mesh;
}

/** Vertical-gradient sky dome. */
export function skyDome(stops: [number, string][]): THREE.Mesh {
  const canvas = document.createElement('canvas');
  canvas.width = 4;
  canvas.height = 256;
  const g2d = canvas.getContext('2d')!;
  const grad = g2d.createLinearGradient(0, 0, 0, 256);
  for (const [offset, color] of stops) grad.addColorStop(offset, color);
  g2d.fillStyle = grad;
  g2d.fillRect(0, 0, 4, 256);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const mat = new THREE.MeshBasicMaterial({ map: tex, side: THREE.BackSide, fog: false });
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(420, 24, 16), mat);
  return mesh;
}

/** Star field for night scenes. */
export function stars(count: number, seedMul = 1): THREE.Points {
  const positions = new Float32Array(count * 3);
  let s = 987654321 * seedMul;
  const rand = () => {
    s = (s * 16807) % 2147483647;
    return s / 2147483647;
  };
  for (let i = 0; i < count; i++) {
    const theta = rand() * Math.PI * 2;
    const phi = rand() * Math.PI * 0.45;
    const r = 400;
    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.cos(phi) + 10;
    positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const mat = new THREE.PointsMaterial({
    color: 0xdfe8ff,
    size: 1.6,
    sizeAttenuation: false,
    transparent: true,
    opacity: 0.85,
    fog: false,
  });
  const points = new THREE.Points(geo, mat);
  points.frustumCulled = false;
  return points;
}

/** Directional "sun" (or moon) with shadow frustum sized to the play area. */
export function sun(
  color: THREE.ColorRepresentation,
  intensity: number,
  x: number,
  y: number,
  z: number,
  extent = 60,
): THREE.DirectionalLight {
  const light = new THREE.DirectionalLight(color, intensity);
  light.position.set(x, y, z);
  light.castShadow = true;
  light.shadow.camera.left = -extent;
  light.shadow.camera.right = extent;
  light.shadow.camera.top = extent;
  light.shadow.camera.bottom = -extent;
  light.shadow.camera.near = 1;
  light.shadow.camera.far = 400;
  light.shadow.bias = -0.0004;
  return light;
}

/** Dispose every geometry/material/texture under a scene. */
export function disposeScene(scene: THREE.Scene): void {
  scene.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const mats = Array.isArray(mesh.material)
      ? mesh.material
      : mesh.material
        ? [mesh.material]
        : [];
    for (const m of mats) {
      const anyMat = m as THREE.MeshStandardMaterial;
      anyMat.map?.dispose();
      anyMat.emissiveMap?.dispose();
      m.dispose();
    }
  });
}
